import React, { useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom'; // Add this import
import {
  Box,
  Container,
  Title,
  Text,
  // List,
  // Code,
  Space,
  Paper,
  Group,
  useMantineTheme,
  useMantineColorScheme,
  Accordion,
  Anchor,
  Stack,
  Affix,
  Transition,
  ActionIcon,
} from '@mantine/core';
import { useWindowScroll } from '@mantine/hooks';
import { IconClipboardList, IconListDetails, IconArrowUp, IconHeadphones } from '@tabler/icons-react';
import { useBackgroundImage } from '../../context/BackgroundImageContext';
import backgroundImageFile from '../../assets/images/brochure-background.jpg';

import StaticGuidelineContent from './StaticGuidelineContent';

// Helper component to create consistent IDs for anchor links
// In: src/components/Pages/GuidelinesPage.js
// AT THE TOP WITH YOUR OTHER IMPORTS

// import SectionTitle from '../common/SectionTitle';

// Helper array for building the Contents accordion dynamically
const mainSections = [
  { number: '', title: ' .' },
  { number: '0.1', title: 'SECTION A: EDITORIAL STYLE GUIDE' },
  { number: '', title: ' .' },
  { number: '1', title: 'Overview' },
  { number: '2', title: 'Useful links' },
  { number: '3', title: 'Abbreviations' },
  { number: '4', title: 'Architectural and artistic periods/styles' },
  { number: '5', title: 'Australia content' },
  { number: '6', title: 'Banned images' },
  { number: '7', title: 'Banned words' },
  { number: '8', title: 'Brand specifics (style/tone)' },
  { number: '9', title: 'Canada content' },
  { number: '10', title: 'Capitalisation' },
  { number: '11', title: 'Caption length' },
  { number: '12', title: 'Caption titles' },
  { number: '13', title: 'Colons & semi-colons' },
  { number: '14', title: 'Companies' },
  { number: '15', title: 'Contractions' },
  { number: '16', title: 'Currency & prices' },
  { number: '17', title: 'Dashes' },
  { number: '18', title: 'Dates & times' },
  { number: '19', title: 'Ellipsis' },
  { number: '20', title: 'Food & drink names' },
  { number: '21', title: 'Foreign words' },
  { number: '22', title: 'Hyperlinks' },
  { number: '23', title: 'Hyphens' },
  { number: '24', title: 'Exceptions' },
  { number: '25', title: 'Image credits and file names' },
  { number: '26', title: 'Measurements' },
  { number: '27', title: 'Notes to syndication' },
  { number: '28', title: 'Numbers' },
  { number: '29', title: 'Overused/unnecessary words and tautology' },
  { number: '30', title: 'People (and cultural sensitivities, disability, etc)' },
  { number: '31', title: 'Places' },
  { number: '32', title: 'Possessive apostrophes' },
  { number: '33', title: 'Punctuation' },
  { number: '34', title: 'Quotes' },
  { number: '35', title: 'Ranking' },
  { number: '36', title: 'Recipes in captions' },
  { number: '37', title: 'Special characters' },
  { number: '38', title: 'Spellings: A-Z' },
  { number: '39', title: '[NB this is for British English]' },
  { number: '40', title: 'Syntax' },
  { number: '41', title: 'Temperatures' },
  { number: '42', title: 'Titles (books, film, TV etc)' },
  { number: '43', title: 'Writing for multiple markets: things to consider' },
  { number: '', title: ' .' },
  { number: '0.2', title: 'SECTION B: PUBLISHING GUIDELINES' },
  { number: '', title: ' .' },
  { number: '44', title: 'Recap' },
  { number: '45', title: 'Prohibited Content' },
  { number: '46', title: 'Content Attribution and Representation' },
  { number: '47', title: 'Content Rights and Responsibility' },
  { number: '48', title: 'Content Formatting' },


  // Add other main section numbers and titles here as you transcribe them
];

const GuidelinesPage = () => {
  // --- Style Hooks & Variables (from FICSPage for consistency) ---
  const theme = useMantineTheme();
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === 'dark';
  const { isBackgroundImageVisible } = useBackgroundImage();
  const [scroll, scrollTo] = useWindowScroll();
  const sectionContainerBg = isDark ? theme.colors.dark[6] : "gray.1";
  const textOnBackgroundImageColor = isBackgroundImageVisible ? theme.white : (isDark ? theme.white : theme.black);
  const location = useLocation(); // Gets the current URL details

  // In: src/components/Pages/GuidelinesPage.js

  // A new, reusable function to handle all smooth scrolling
  // Wrap the function definition in useCallback with an empty dependency array []
  const handleSmoothScroll = useCallback((event, targetId) => {
    if (event) event.preventDefault();

    const id = targetId.replace('#', '');
    const element = document.getElementById(id);

    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  }, []); // <-- The empty array at the end is important

  useEffect(() => {
    if (location.hash) {
      // We wrap the scroll command in a setTimeout to give the browser
      // a moment to finish rendering the page's content.
      const timer = setTimeout(() => {
        handleSmoothScroll(null, location.hash);
      }, 300); // A 100ms delay is usually plenty of time.

      // This is a standard React cleanup function. If the user navigates away
      // before the 100ms is up, it cancels the timer to prevent errors.
      return () => clearTimeout(timer);
    }
  }, [location.hash, handleSmoothScroll]);

  // --- End Style Hooks & Variables ---

  return (
    <Box
      style={{
        width: '100%',
        flexGrow: 1,
        display: 'flex',
        flexDirection: 'column',
        backgroundImage: isBackgroundImageVisible ? `url(${backgroundImageFile})` : 'none',
        backgroundColor: !isBackgroundImageVisible ? (isDark ? theme.colors.dark[7] : theme.colors.gray[0]) : 'transparent',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundAttachment: 'fixed',
        overflow: 'auto',
        //scrollBehavior: 'smooth',
      }}
    >
      <Container size="lg" py="xl" style={{ width: '100%', flexGrow: 1 }}>
        {/* --- Page Header (from FICSPage) --- */}
        <Title order={1} ta="center" style={{ color: textOnBackgroundImageColor }}>
          <Group justify="center" gap="xs">
            <IconClipboardList size={32} stroke={1.5} color={theme.colors.yellow[isDark || isBackgroundImageVisible ? 4 : 6]} />
            Editorial Style & Publishing Guidelines
          </Group>
        </Title>
        <Text ta="center" mt="sm" style={{ color: textOnBackgroundImageColor }}>
          Our content standards and practices. Numbered for cross-referencing only.
        </Text>

        <Paper withBorder p="xl" radius="md" shadow="sm" mt="xl" bg={isDark ? theme.colors.dark[6] : "white"}>
          <Group justify="flex-start" gap="xs">
            <IconHeadphones size={20} stroke={1.5}  />
          <Title order={3}>Audio Overview</Title>
          </Group>
          <Text size="sm" c="dimmed" mb="xl">
            Listen to an AI-generated discussion of our guidelines.
          </Text>
          {/* <audio controls style={{ width: '100%' }}> */}
          <audio controls style={{ width: '99%', display: 'block', marginLeft: 'auto', marginRight: 'auto' }}>
            <source src="https://firebasestorage.googleapis.com/v0/b/freelancer-ideas-portal.firebasestorage.app/o/audio%2FLoveInc%20Editorial%20Style%20Guide%20Mono.mp3?alt=media&token=0bb56557-535c-4ef1-9c1f-48cb9a8d5cb5" type="audio/mpeg" />
            Your browser does not support the audio element.
          </audio>
        </Paper>


        {/* --- Main Content Paper (from FICSPage) --- */}
        <Paper withBorder shadow="md" p="xl" radius="md" mt="xl" bg={sectionContainerBg}>
          <Text c="dimmed" ta="right" mb="xl">Last updated: Jan 2025</Text>

          {/* NEW: Collapsible "Contents" Accordion with correct structure */}
          {/* UPDATED: Accordion without the conflicting 'variant' prop */}

          {/* STEP 1: A new Paper just for the Accordion */}
          <Paper withBorder p="md" radius="md" shadow="xs">
            <Accordion
              variant="contained" // A better variant for this context
              radius="md"
              defaultValue="contents"
            >
              <Accordion.Item value="contents">
                <Accordion.Control icon={<IconListDetails size={20} />}>
                  <Title order={3}>Contents</Title>
                </Accordion.Control>
                {/* 
                <Accordion.Panel>
                  <Stack gap="sm">
                    {mainSections.map((sec) => {
                      const targetId = `#section-${sec.number.replace(/\./g, '-')}`;
                      return (
                        <Anchor
                          key={sec.number}
                          href={targetId}
                          onClick={(e) => handleSmoothScroll(e, targetId)} // <-- THIS IS THE CHANGE
                        >
                          {sec.number}. {sec.title}
                        </Anchor>
                      );
                    })}
                  </Stack>
                </Accordion.Panel>
               
                  */}
                <Accordion.Panel>
                  <Stack gap="sm">
                    {mainSections.map((sec, index) => {
                      // Case 1: Handle spacers
                      if (sec.number === '') {
                        return <Space key={`spacer-${index}`} h="xs" />;
                      }

                      const targetId = `#section-${sec.number.replace(/\./g, '-')}`;

                      // Case 2: Handle main section titles (numbers starting with "0.")
                      if (sec.number.startsWith('0.')) {
                        return (
                          <Anchor
                            key={`${index}-${sec.number}`}
                            href={targetId}
                            onClick={(e) => handleSmoothScroll(e, targetId)}
                          >
                            {/* Render title only, and make it bold */}
                            <Text size="lg" component="span" fw={700}>
                              {sec.title}
                            </Text>
                          </Anchor>
                        );
                      }

                      // Case 3: Handle all other regular items
                      return (
                        <Anchor
                          key={`${index}-${sec.number}`}
                          href={targetId}
                          onClick={(e) => handleSmoothScroll(e, targetId)}
                          pl="xl"
                        >
                          {sec.number}. {sec.title}
                        </Anchor>
                      );
                    })}
                  </Stack>
                </Accordion.Panel>
              </Accordion.Item>
            </Accordion>
          </Paper>

          {/* STEP 2: The main content 
          is now separate */}

          {/* NEW, OPTIMIZED COMPONENT BELOW */}
          <StaticGuidelineContent onAnchorClick={handleSmoothScroll} />

        </Paper>
      </Container>

      {/* Experimenting w/ a 2nd arrow on left...

      <Affix position={{ bottom: 50, left: 50 }}>
        <Transition transition="slide-up" mounted={scroll.y > 200}>
          {(transitionStyles) => (
            <ActionIcon
              color="pink"
              variant="filled"
              size="xl"
              style={transitionStyles}
              onClick={() => scrollTo({ y: 0, behavior: 'smooth' })} // <-- THIS IS THE CHANGE
              aria-label="Scroll to top"
            >
              <IconArrowUp size={20} />
            </ActionIcon>
          )}
        </Transition>
      </Affix>
      */}

      <Affix position={{ bottom: 50, right: 50 }}>
        <Transition transition="slide-up" mounted={scroll.y > 200}>
          {(transitionStyles) => (
            <ActionIcon
              color="pink"
              variant="filled"
              size="xl"
              style={transitionStyles}
              onClick={() => scrollTo({ y: 0, behavior: 'smooth' })} // <-- THIS IS THE CHANGE
              aria-label="Scroll to top"
            >
              <IconArrowUp size={20} />
            </ActionIcon>
          )}
        </Transition>
      </Affix>
    </Box >
  );
};

export default GuidelinesPage;